<template>
  <div id="CampaignEditOffersList">

    <Page
      :title="`${$t('Editing')} '${campaign.name}'`"
      :secondaryActions="[{
        label: $t('Notes'),
        onAction: () => { eventHub.$emit('showCampaignNotes', campaign.id) }
      }]"
      :primaryAction="{
        label: $t('Add Offer'),
        onAction: () => addOffer(),
        disabled: saving
      }"
      size="fullWidth"
    >   
      <campaign-edit-header :campaign="campaign"></campaign-edit-header>
      <LoadingLayout v-if="loading" />
      <Layout v-if="!loading">
        <LayoutSection>
          <Card 
            :title="$t('Offers')"
            :actions="[
              {
                label: $t('Export'),
                onAction: () => exportOffers(),
                disabled: saving
              }
            ]"
          >
            <Filters
              ref="filters"
              :filters="[
                {
                  key: 'status',
                  label: $t('Status'),
                  type: 'select',
                  multiple: true,
                  options: [
                    { label: $t('Active'), value: 'enabled' },
                    { label: $t('Disabled'), value: 'disabled' }
                  ]
                },
                {
                  key: 'segmentId',
                  label: $t('Segments'),
                  type: 'select',
                  options: formattedSegmentList,
                  multiple: true
                }
              ]"
              :applied-filters="filter"
              @update:applied-filters="updateFilter"
              @update:query="setQuery"
              :query.sync="stringFilter"
              required
            />
            <DataTable
              :columns="[
                {
                  key: 'id',
                  label: $t('ID'),
                  fixed: true,
                  sortable: true
                },
                {
                  key: 'displayText',
                  label: $t('Display'),
                  sortable: true
                },
                {
                  key: 'code',
                  label: $t('Code'),
                  sortable: true
                },
                {
                  key: 'status',
                  label: $t('Status')
                },
                {
                  key: 'min',
                  label: $t('Min'),
                  sortable: true
                },
                {
                  key: 'validFromDate',
                  label: $t('Start'),
                  format: 'dateShort',
                  sortable: true
                },
                {
                  key: 'expiryDate',
                  label: $t('End'),
                  format: 'dateShort',
                  sortable: true
                },
                {
                  key: 'variant',
                  label: $t('Variant'),
                  sortable: true
                },
                {
                  key: 'presented',
                  label: $t('Presented')
                },
                {
                  key: 'fullLanguage',
                  label: $t('Language')
                },
                {
                  key: 'numSegs',
                  label: $t('# Segs'),
                  sortable: true
                }
              ]"
              :rows="formattedIncentives"
              :row-actions="row => (getRowActions(row))"
              :sort.sync="sortBy"
              :bulk-actions="bulkActions"
              :selected-ids.sync="selectedIds"
              :pagination="{
                offset: offset,
                limit: limit,
                total: formattedIncentives.length,
                updateOffset: val => offset = val,
                updateLimit: updateLimit
              }"
              compact
            >
              <template v-slot:col:displayText="{row}">
                {{row.displayText}}
                <Caption v-if="row.condition">{{row.condition}}</Caption>
              </template>
              <template v-slot:col:id="{row}">
                <!-- <Link :url="getSegmentUrl(row)">{{row.name}}</Link> -->
                <PageLink v-if="!row.isDisabled" :href="getOfferUrl(row)">{{row.id}}</PageLink>
                <template v-else>{{row.id}}</template>
              </template>
              <template v-slot:col:code="{row}">
                <Badge v-if="row.promoType === 'file'" status="info">{{row.code}}</Badge>
                <template v-else-if="row.promoType === 'code'">{{row.code}}</template>
              </template>
              <template v-slot:col:status="{row}">
                <Badge v-if="!row.isDisabled" status="success">{{$t('Active')}}</Badge>
                <Badge v-else-if="row.isDisabled" status="warning">{{$t('Inactive')}}</Badge>
                <div><Badge v-if="row.status === 'runOut'" status="attention">{{$t('campaignEdit.offer.Low Codes')}}</Badge></div>
                <div><Badge v-if="outsideValidityPeriod(row)" status="info">{{$t('campaignEdit.segment.Outside validity period')}}</Badge></div>
              </template>
              <template v-slot:col:numSegs="{row}">
                <template v-if="row.numSegs">
                  <PageLink @click="showSegments(row)">{{row.numSegs}}</PageLink>
                </template>
                <template v-else>0</template>
              </template>
              <template v-slot:col:variant="{row}">
                <Badge v-if="row.variant && !validVariant(row.variant)" status="warning" :tooltip="$t('campaignEdit.offer.listVariantToolTip')">{{row.variant}}</Badge>
                <template v-else-if="row.variant">{{row.variant}}</template>
                <template v-else>—</template>
              </template>
            </DataTable>
          </Card>

          <Card v-if="singleUseCouponsEnabled && hasSingleUseCoupons">
            <CardSection>
              <em><div v-html="$t('campaignEdit.offer.lowCodesThresholdMessage', { value: `${singleUseDisableThreshold}`} )"></div></em>
            </CardSection>
          </Card>
        </LayoutSection>
      </Layout>
      <!-- <pre>{{formattedIncentives}}</pre> -->



    </Page>
  </div>
</template>

<script>

import UI from 'fanplayr-ui'
import _ from 'lodash'

import Utils from '../classes/utils/Utils'
import Downloader from '../classes/utils/Downloader'
import CampaignEditHeader from '@/components/CampaignEditHeader'

export default {
  name: 'CampaignEditOffersList',
  props: {
    campaign: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: true,
      saving: false,
      filter: {
        status: [],
        segmentId: []
      },
      offset: 0,
      limit: 10,
      stringFilter: null,
      filterEnableDisable: null,
      filterSegmentId: null,
      widgetInfos: null,
      segments: [],
      incentives: [],
      languages: [],
      selectedIds: [],
      singleUseCouponsEnabled: false,
      singleUseDisableThreshold: 0,
      sortBy: {
        column: 'id',
        direction: 'asc'
      }
    }
  },
  mounted() {
    NProgress.start()
    // console.log(this.$route)

    this.setFilterFromQuery()

    this.$watch('$route', () => {
      this.setFilterFromQuery()
    }, { deep: true })

    this.limit = this.getTableLimit('tableLimit') || 10
    Promise.all([ 
      this.api.getSegments(this.campaign.id), 
      this.api.getIncentives(this.campaign.id),
      this.api.getLanguageList(),
      this.api.getCurrentAccountPrefs(),
      this.api.getSingleUseDisableThreshold(this.campaign.id),
      this.api.getWidgetInfos()
    ]) 
      .then((data) => {
        let [ segments, incentives, languages, prefs, threshold, widgetInfos ] = data
        this.segments = segments
        this.incentives = incentives
        this.languages = languages
        this.singleUseCouponsEnabled = prefs.singleUseCoupons
        this.singleUseDisableThreshold = threshold
        this.widgetInfos = widgetInfos
        this.loading = false
      })
      .catch(this.apiCatch)
      .finally(() => {
        NProgress.done()
      })
  },
  computed: {
    formattedIncentives() {
      let incs = this.incentives.map(i => {
        return {
          ...i,
          code: i.promoType === 'code' ? i.promoCode : this.$t('Multiple'),
          min: i.minPurchase === -1 ? null : i.minPurchase,
          variant: i.settings && i.settings.classes ? i.settings.classes : null,
          presented: i.maxRewards !== -1 ? i.numRewardsUsed + ` ${this.$t('of')} ` + i.maxRewards : UI.format(i.numRewardsUsed, 'integer'),
          fullLanguage: this.languageLookup(i.language),
          numSegs: this.getNumSegments(i)
        }
      }).filter(i => {
        if (!this.filter.status) {
          return true
        }
        if (this.filter.status.includes('disabled') && this.filter.status.includes('enabled')) {
          return true
        }
        if (this.filter.status.includes('disabled')) {
          return i.isDisabled
        }
        if (this.filter.status.includes('enabled')) {
          return !i.isDisabled
        }
        return true
      }).filter(i => {
        if (!this.filter.segmentId || !this.filter.segmentId.length) {
          return true
        }
        for (const id of this.filter.segmentId) {
          if (this.getSegmentIds(i).includes(id)) {
            return true
          }
        }
        return false
      }).filter(i => {
        return this.parseFilter(i)
      })

      
      if (this.sortBy) {
        let iteree = this.sortBy.column
        if (this.sortBy.column === 'variant' || this.sortBy.column === 'code' || this.sortBy.column === 'displayText') {
          iteree = inc => inc[this.sortBy.column] && inc[this.sortBy.column].trim().toLowerCase()
        }
        incs = _.orderBy(incs, [iteree], [this.sortBy.direction])
      }
      return incs
    },
    formattedSegmentList() {
      const rv = this.segments.map(s => {
        return {
          label: s.name,
          value: s.id
        }
      })
      return rv
    },
    bulkActions() {
      const actions = [
        // {
        //   label: this.$t('Enable'),
        //   onAction: () => this.handleBulkAction('enable')
        // },
        // {
        //   label: this.$t('Disable'),
        //   onAction: () => this.handleBulkAction('disable')
        // }
      ]
      if (this.theUser.isSuper) {
        actions.push({
          label: this.$t('campaignEdit.offer.editOffers'),
          onAction: () => this.toBulkEditor()
        })
      }
      return actions
    },
    hasSingleUseCoupons() {
      return this.incentives.filter(d => d.promoType === 'file');
    },
    availableVariants() {
      const variants = this.widgetInfos.reduce((list, info) => {
        if (info.variants.length) {
          list.push(...info.variants);
        }
        return list;
      }, []);
      return [...new Set(variants)]
    }
  },
  methods: {
    setFilterFromQuery() {
      if (this.$route.query) {
        this.filter = {}
        Object.keys(this.$route.query).forEach((k) => {
          // console.log(k, this.$route.query[k])
          if (this.$route.query[k]) {
            if (k === 'status' || k === 'segmentId') {
              if (Array.isArray(this.$route.query[k])) {
                this.filter[k] = k === 'segmentId' ? this.$route.query[k].map(strId => parseInt(strId)) : this.$route.query[k]
              } else {
                this.filter[k] = k === 'segmentId' ? [parseInt(this.$route.query[k])] : [this.$route.query[k]]
              }
            } else {
              this.filter[k] = this.$route.query[k]
            }
          } else {
            this.filter[k] = null
          }
        })
      }
      if (_.isEmpty(this.$route.query)) {
        this.filter.status = ['enabled']
      }
      this.$router.replace({ name: 'CampaignEditOffersList', query: this.filter})
      .catch(this.duplicateRouteCatch)
    },
    validVariant(variant) {
      const foundVariant = this.availableVariants.find(v => v === variant)
      return !!foundVariant
    },
    updateLimit(limit) {
      this.setTableLimit('tableLimit', limit)
      this.limit = limit
      this.offset = 0
    },
    toBulkEditor() {
      this.$router.push({ name: 'OfferBulkEdit', props: { campaign: this.campaign }, query: { id: this.selectedIds } })
    },
    getRowActions(row) {
      const rv = []
      rv.push({
        label: this.$t('Copy'),
        onAction: (inc) => this.copy(inc),
        disabled: !!row.isDisabled || this.saving
      })
      if (row.isDisabled) {
        rv.push({
          label: this.$t('Enable'),
          onAction: (inc) => this.enableOrDisable(inc),
          disabled: this.saving
        })
      } else {
        rv.push({
          label: this.$t('Disable'),
          onAction: (inc) => this.enableOrDisable(inc),
          disabled: this.saving
        })
      }
      rv.push({
        label: this.$t('Delete'),
        onAction: (inc) => this.remove(inc),
        disabled: this.saving
      })
      return rv
    },
    getCodeBadge(row) {
      return row.promoType === 'file' ? {
        status: 'info'
      } : false
    },
    getNumSegments(incentive) {
      return this.getSegmentIds(incentive).length
    },
    getSegmentIds(incentive) {
      let segIds = []
      this.segments.forEach((seg) => {
        if (seg.offerIds) {
          seg.offerIds.forEach((oId) => {
            if (oId === incentive.id) {
              segIds.push(seg.id)
            } 
          })
        }
      })
      return segIds
    },
    resetPagination() {
      this.offset = 0
    },
    setQuery(query) {
      this.stringFilter = query
      this.resetPagination()
    },
    updateFilter(filter) {
      this.filter = filter
      this.resetPagination()
      this.updateUrl()
    },
    showSegments(row) {
      const segments = this.getSegmentIds(row).map(id => this.segments.find(d => d.id === id))
      if (segments.length) {
        const h = this.$createElement
        this.$ui.alert({
          title: this.$t(`campaignEdit.offer.segmentsInOffer`, { value: `#${row.id} — ${row.displayText}` }),
          content: h('List', segments.map(d => {
            return h('ListItem', [
              h('a', {
                attrs: {
                  href: `/#/campaigns/${this.campaign.id}/segmentation/segment/${d.id}`,
                  target: '_blank'
                }
              }, d.name),
              // h('Caption', this.getOfferDesc(d))
            ])
          }))
        })
      }
    },
    updateUrl() {
      this.$router.push({ name: 'CampaignEditOffersList', query: this.filter})
      .catch(this.duplicateRouteCatch)
    },
    exportOffers() {
      const filename = `offers__${window.moment().format('YYYY-MM-DD')}.csv`
      const data = []
      data.push([
        'Value',
        'Value Type',
        'Code Type',
        'Code File',
        'Code',
        'Anticipated Retail Value',
        'Offer Text',
        'Minimum Purchase',
        'Additional Comments',
        'Language',
        'Start Date',
        'Start Time',
        'End Date',
        'End Time',
        'Limit',
        'Image',
        'Variant',
        'Campaign ID'
      ])

      this.incentives.forEach(inc => {
        data.push([
          inc.value,
          inc.valueType,
          inc.promoType,
          inc.fileName ? inc.fileName : '',
          inc.promoType === 't' ? this.$t('FILES').toUpperCase() : inc.promoCode,
          inc.notionalValue,
          inc.displayText,
          inc.minPurchase === -1 ? '' : inc.minPurchase,
          inc.condition,
          inc.language,
          inc.validFromDate,
          inc.validFromDateT ? inc.validFromDateT : '',
          inc.expiryDate,
          inc.expiryDateT ? inc.expiryDateT : '',
          inc.maxRewards,
          inc.imageFile,
          inc.settings && inc.settings.classes,
          this.campaign.id
        ])
      })
      
      Downloader.download(Utils.arrayToCsvString(data), filename, 'text/csv')
    },
    getOfferUrl(incentive) {
      return `/#/campaigns/${this.campaign.id}/offers/edit/${incentive.id}`
    },
    addOffer() {
      this.$router.push({ name: 'CampaignEditOffersCreate' })
    },
    copy(incentive) {
      this.$ui.confirm({
        title: this.$t('Copy Offer'),
        content: this.$t('campaignEdit.offer.Are you sure you want to copy offer', { value: `${incentive.displayText} (${incentive.code})`  }),
        onConfirm: () => {
          const copiedIncentive = this.incentives.find(i => i.id === incentive.id)
          NProgress.start()
          this.saving = true
          this.api.copyIncentive(copiedIncentive)
            .then((inc) => {
              this.incentives.push(inc)
            })
            .catch(this.apiCatch)
            .finally(() => {
              this.saving = false
              NProgress.done()
            })
        }
      })
    },
    async remove(incentive) {
      const confirm = await this.$ui.confirm({
        title: this.$t('Confirm Delete'),
        content: this.$t('campaignEdit.offer.Are you sure you want to delete offer', { value: `${incentive.displayText} (${incentive.code})`  })
      })
      if (!confirm) {
        return
      }
      if (this.getNumSegments(incentive)) {
        // must remove offer from all segments before deleting
        this.$ui.alert({
          title: this.$t('Alert'),
          content: this.$t('campaignEdit.offer.removeFromAllSegmentsWarning')
        })
        return;
      }
      NProgress.start()
      this.saving = true
      this.api.deleteIncentive(incentive.id, this.campaign.id)
        .then(() => {
          this.incentives = this.incentives.filter(i => i.id !== incentive.id)
          this.$ui.toast({ title: this.$t('Offer Removed') })
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
          NProgress.done()
        })

    },
    async enableOrDisable(incentive) {
      const confirm = await this.$ui.confirm({
        title: incentive.isDisabled ? this.$t('Confirm Enable') : this.$t('Confirm Disable'),
        content: incentive.isDisabled ? 
          this.$t('campaignEdit.offer.Are you sure you want to enable offer', { value: `${incentive.displayText} (${incentive.code})`  }) 
          : this.$t('campaignEdit.offer.Are you sure you want to disable offer', { value: `${incentive.displayText} (${incentive.code})`  })
      })
      if (!confirm) {
        return
      }
      if (incentive.isDisabled) {
        incentive.isDisabled = false
      } else {
        incentive.isDisabled = true
      }

      NProgress.start()
      this.saving = true
      this.api.toggleIncentive(incentive.id, this.campaign.id, incentive.isDisabled)
        .then(() => {
          const i = this.incentives.find(i => i.id === incentive.id)
          i.isDisabled = incentive.isDisabled
          this.$ui.toast({ title: this.$t('Updated') })
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
          this.saving = false
        })
    },
    languageLookup(value) {
      var lang = this.languages.find(l => l.value === value)
      return lang.label
    },
    parseFilter(inc) {
      const filter = this.stringFilter && this.stringFilter.trim()
      if (filter && filter.length){
        var filters = filter.split(' ');
        for (var i = 0; i < filters.length; i++) {
          var currentFilter = filters[i];
          var idMatch = /^id:(\d+)$/.exec(currentFilter);
          if (idMatch) {
            if (inc.id === +idMatch[1]) {
              return true
            }
          } else {
            var pattern = new RegExp(_.escapeRegExp(currentFilter), 'i');
            const bool = pattern.test(inc.displayText) ||
              pattern.test(inc.condition) ||
              pattern.test(inc.code) ||
              pattern.test(inc.settings && inc.settings.classes) ||
              pattern.test(inc.language) ||
              pattern.test(this.languageLookup(inc.language)) ||
              pattern.test(inc.validFromDate) ||
              pattern.test(inc.expiryDate)
            if (bool) { 
              return true
            }
          }
        }
        return false
      }
      return true
    }      
  },
  components: {
    CampaignEditHeader
  }
}

</script>


